import { api } from '../api/client';
import type { CareProfile, CareReminderStatus, CareReminderView, CareState, CareSubmitResult, PendingCareItem, UpcomingCareItem } from './types/care.types';

export const careApi = {
  async pending(opts: { includeAnswered?: boolean } = {}): Promise<{ items: PendingCareItem[]; profile: CareProfile; upcoming: UpcomingCareItem[] }> {
    const d = await api.get<{ items: PendingCareItem[]; profile: CareProfile; upcoming?: UpcomingCareItem[] }>('/api/care/items/pending', { params: opts.includeAnswered ? { includeAnswered: 'true' } : undefined });
    // `upcoming` (A128) não vem de API mais velha: lista vazia, e o banner de próximo some.
    return { items: d?.items ?? [], profile: d?.profile ?? ({} as CareProfile), upcoming: d?.upcoming ?? [] };
  },
  item: (id: string) => api.get<PendingCareItem>(`/api/care/items/${id}`),
  /** `idempotencyKey` evita que retry de rede ou duplo toque virem duas rodadas. */
  submit: (id: string, answers: Record<string, unknown>, idempotencyKey: string) => api.post<CareSubmitResult>(`/api/care/items/${id}/responses`, { answers, idempotencyKey }),
  reminders: () => api.get<CareReminderView[]>('/api/care/reminders').then((r) => (Array.isArray(r) ? r : [])),
  answerReminder: (id: string, payload: { status: CareReminderStatus; scheduledAt?: string | null; fields?: Record<string, unknown>; recordId?: string; optionId?: string }) =>
    api.post<void>(`/api/care/reminders/${id}/answer`, payload),
  state: () => api.get<CareState>('/api/care/state'),
};

export interface CareToolSummary {
  key: string;
  label: string;
  description?: string;
  kind: string;
  ativa: boolean;
  ultimo: { occurredAt: string; payload: any } | null;
  /**
   * O texto de abertura da ferramenta (A059), já escolhido pela API para a
   * jornada da paciente. `null` ou ausente (API mais velha) = sem abertura.
   */
  opening?: string | null;
}
export interface CareToolRecord {
  id: string;
  occurredAt: string;
  payload: any;
  createdAt?: string;
}

export const careToolsApi = {
  list: () => api.get<{ tools: CareToolSummary[] } | CareToolSummary[]>('/api/care/tools').then((r) => (Array.isArray(r) ? r : r?.tools ?? [])),
  records: (key: string) => api.get<{ records: CareToolRecord[] } | CareToolRecord[]>(`/api/care/tools/${key}/records`).then((r) => (Array.isArray(r) ? r : r?.records ?? [])),
  create: (key: string, payload: any, occurredAt?: string) => api.post<CareToolRecord>(`/api/care/tools/${key}/records`, { payload, occurredAt }),
  remove: (key: string, recordId: string) => api.delete<void>(`/api/care/tools/${key}/records/${recordId}`),
  /** Edita um registro avulso (A131): o "Editar" das entradas do diário. */
  update: (key: string, recordId: string, payload: any, occurredAt?: string | null) => api.put<CareToolRecord>(`/api/care/tools/${key}/records/${recordId}`, { payload, occurredAt }),
};

/**
 * Fotos e documentos privados das ferramentas (A131). O envio devolve só o id,
 * que o registro guarda; a leitura volta os bytes, porque nenhum endereço de
 * arquivo privado sai da API.
 */
export const midiaPrivadaApi = {
  enviar: (arquivo: Blob, nome = 'foto.jpg') => {
    const corpo = new FormData();
    corpo.append('arquivo', arquivo, nome);
    return api.post<{ id: string; tipo: string }>('/api/care/midias', corpo);
  },
  ler: async (id: string): Promise<Blob> => (await api.raw.get(`/api/care/midias/${encodeURIComponent(id)}`, { responseType: 'blob' })).data as Blob,
};

/**
 * O áudio de inspiração de uma ferramenta de escrita (A134): o da marca, o da
 * Eva, ou nulo — e aí o app mostra a frase escrita do ponto de partida.
 */
export const inspiracaoApi = {
  daFerramenta: (toolKey: string) =>
    api.get<{ url: string; origem: 'MARCA' | 'EVA' } | null>(`/api/care/tools/${toolKey}/inspiracao`).catch(() => null),
};
